<template>
  <Form
    :form-id="formId"
  >
    <VPCombobox
      input-id="artistName"
      :options="artistOptions"
      :label="_label"
      :clearable="true"
      :placeholder="$gettext('Start typing to search')"
      allow-add
      disable-filter
      :options-loading="artistSearchLoading"
      @add="onAdd"
      @change="onSelect"
      @search="onArtistSearch"
    >
      <template #item="{ option }">
        <ArtistDropdownItem :artist="option.value" />
      </template>
    </VPCombobox>
  </Form>
</template>

<script setup lang="ts">
import { useGettext } from 'vue3-gettext';
import { Form, useFormsStoreComposable } from 'vuniform';
import VPCombobox from '@/components/form/VPCombobox.vue';
import { computed } from 'vue';
import { useArtistStore } from '@/stores/artists';
import { LoadingState } from '@/util/LoadingState';
import ArtistDropdownItem from '@/components/artist/ArtistDropdownItem.vue';
import { API } from '@vp/common';

type Props = {
  eventId: number;
  label?: string;
} | {
  eventGroupId: number;
  label?: string;
};

const props = defineProps<Props>();

type ArtistEntry = API.Artist.SearchResultArtist.SearchResultArtist;

const emit = defineEmits<{
  (e: 'add', artist: ArtistEntry): void;
  (e: 'select', artist: ArtistEntry): void;
}>();

const artistStore = useArtistStore();
const { $gettext } = useGettext();
const { formId } = useFormsStoreComposable('eventGroupArtistNew');

const _label = computed(() => props.label || $gettext('Artist'));

const onSelect = (artist: ArtistEntry) => {
  emit('select', artist);
};

const onAdd = ({ value }: { value: string }): void => {
  emit('add', { name: value });
};

const onArtistSearch = (name: string): void => {
  artistStore.artistSearchFilter = name;
};

const artistOptions = computed(() =>
  artistStore.searchedArtists.map(artist => ({
    name: artist.name,
    value: artist,
  })),
);

const artistSearchLoading = computed(() => artistStore.artistSearchLoading === LoadingState.LOADING);
</script>
